import React from 'react';
import {
  Box,
  TextField,
  IconButton,
  Chip,
  Typography,
  InputAdornment,
  Tooltip,
} from '@mui/material';
import { X, Clock, Calendar, User } from 'lucide-react';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import type { User as UserType } from '@/types';
import { AppSelect, AppMenuItem } from '@/shared/components/AppSelect';

interface SubtaskFormData {
  title: string;
  estimatedTime: number; // minutes
  dueDate: Dayjs | null;
  responsible: string[];
}

interface SubtaskFormProps {
  index: number;
  data: SubtaskFormData;
  onChange: (index: number, data: SubtaskFormData) => void;
  onRemove: (index: number) => void;
  availableUsers: UserType[]; // RACI pool from parent
  parentDueDate: Dayjs;
  parentStartDate: Dayjs | null;
  remainingTime: number; // minutes
  errors?: {
    title?: string;
    estimatedTime?: string;
    dueDate?: string;
    responsible?: string;
  };
}

function userDisplayName(u: UserType): string {
  return u.displayName || [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;
}

/**
 * SubtaskForm
 * Single subtask entry form with validation
 * Enforces time budget and RACI inheritance
 */
const SubtaskForm: React.FC<SubtaskFormProps> = ({
  index,
  data,
  onChange,
  onRemove,
  availableUsers,
  parentDueDate,
  parentStartDate,
  remainingTime,
  errors,
}) => {
  const handleChange = (field: keyof SubtaskFormData, value: any) => {
    onChange(index, { ...data, [field]: value });
  };

  const maxAllowedTime = Math.min(
    data.estimatedTime + remainingTime,
    remainingTime > 0 ? data.estimatedTime + remainingTime : data.estimatedTime
  );

  return (
    <Box
      sx={{
        p: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        bgcolor: 'background.paper',
        position: 'relative',
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Subtask {index + 1}
        </Typography>
        <IconButton size="small" onClick={() => onRemove(index)} color="error">
          <X size={16} />
        </IconButton>
      </Box>

      {/* Title */}
      <TextField
        fullWidth
        size="small"
        label="Subtask Title"
        value={data.title}
        onChange={(e) => handleChange('title', e.target.value)}
        error={Boolean(errors?.title)}
        helperText={errors?.title}
        sx={{ mb: 2 }}
        placeholder="e.g., Setup database schema"
      />

      {/* Bottom Row: Time, Due Date, Assignee */}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {/* Estimated Time */}
        <TextField
          size="small"
          label="Time (min)"
          type="number"
          value={data.estimatedTime}
          onChange={(e) => handleChange('estimatedTime', parseInt(e.target.value) || 0)}
          error={Boolean(errors?.estimatedTime)}
          helperText={errors?.estimatedTime || (remainingTime < 0 ? `Exceeds by ${Math.abs(remainingTime)} min` : `${remainingTime} min left`)}
          sx={{ flex: '1 1 100px', minWidth: '100px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Clock size={16} />
              </InputAdornment>
            ),
          }}
          inputProps={{
            min: 0,
            max: maxAllowedTime,
          }}
        />

        {/* Due Date */}
        <Box sx={{ flex: '1 1 180px', minWidth: '180px' }}>
          <DateTimePicker
            label="Due Date"
            value={data.dueDate}
            onChange={(newValue) => handleChange('dueDate', newValue)}
            maxDate={parentDueDate}
            minDate={parentStartDate || undefined}
            slotProps={{
              textField: {
                size: 'small',
                fullWidth: true,
                error: Boolean(errors?.dueDate),
                helperText: errors?.dueDate,
              },
            }}
          />
        </Box>

        {/* Responsible (from parent RACI pool) */}
        <Box sx={{ flex: '1 1 200px', minWidth: '200px' }}>
          <AppSelect
            label="Responsible"
            size="small"
            error={Boolean(errors?.responsible)}
            formControlProps={{ sx: { width: '100%' } }}
            multiple
            value={data.responsible}
            onChange={(e) => handleChange('responsible', e.target.value)}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((userId) => {
                  const user = availableUsers.find((u) => u.id === userId);
                  return (
                    <Chip
                      key={userId}
                      label={user ? userDisplayName(user) : userId}
                      size="small"
                    />
                  );
                })}
              </Box>
            )}
          >
            {availableUsers.map((user) => (
              <AppMenuItem key={user.id} value={user.id}>
                {userDisplayName(user)}
              </AppMenuItem>
            ))}
          </AppSelect>
          {errors?.responsible && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
              {errors.responsible}
            </Typography>
          )}
          {availableUsers.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1.5 }}>
              Set parent task RACI first
            </Typography>
          )}
        </Box>
      </Box>

      {/* Remaining Budget Warning */}
      {remainingTime < 0 && (
        <Box
          sx={{
            mt: 1,
            p: 1,
            bgcolor: 'error.lighter',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="caption" color="error.main">
            ⚠️ This subtask exceeds parent time budget by {Math.abs(remainingTime)} minutes
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SubtaskForm;
